import { Maybe } from "graphql/jsutils/Maybe";
import { useNotificationStore } from "../components/Notifications/notification-store";
import { createAuthorisedApiRequest } from "../helpers/create-authorised-request";
import { fetchWrapper } from "../helpers/fetch-wrapper";

type TFileInfo = {
  id: string;
  name: Maybe<string>;
  fileExtension: Maybe<string>;
};

type TEndpointRequest = {
  ConvertToPdf: boolean;
  FileId: string;
}[];

type TEndpointResponse = {
  fileId: string;
  url: string;
}[];

const guardIsEndpointResponse = (thing: unknown): thing is TEndpointResponse => {
  if (!Array.isArray(thing)) {
    return false;
  }

  for (const i of thing) {
    if (typeof i !== "object" || typeof i["fileId"] !== "string" || typeof i["url"] !== "string") {
      return false;
    }
  }

  return true;
};

export const getFolderService = () => ({
  createNewFolder,
});

export type TFolderService = ReturnType<typeof getFolderService>;

// returns true if success
export const createNewFolder = async (
  name: string,
  id: string,
  matterId: string,
  parentId: string
): Promise<boolean> => {
  if (!name) {
    name = "New Folder";
  }

  const payload = {
    FolderName: name,
    FolderId: id,
    MatterId: matterId,
    ParentFolderId: parentId,
  };

  const { url, requestInit } = await createAuthorisedApiRequest("CreateFolderUrl", payload);

  const result = await fetchWrapper(url, requestInit);

  if (result.err) {
    useNotificationStore.getState().errorNotification("Failed to create folder", result.err);
    return false;
  }

  return true;
};

export const attachFilesToDraft = async (infos: TFileInfo[]) => {
  const item = Office.context.mailbox.item;
  if (!item) {
    useNotificationStore.getState().errorNotification("Could not find mail item");
    return;
  }
  if (!infos || !infos.length) {
    return;
  }
  const payload: TEndpointRequest = infos.map((i) => ({ ConvertToPdf: false, FileId: i.id }));

  const { url, requestInit } = await createAuthorisedApiRequest("FileDownloadUrls", payload);
  const result = await fetchWrapper(url, requestInit);

  if (result.err) {
    useNotificationStore.getState().errorNotification("Failed to add attachments", result.err);
  } else if (result.result) {
    const payload = result.result;
    if (!guardIsEndpointResponse(payload)) {
      useNotificationStore
        .getState()
        .errorNotification("Failed to attach items - bad payload", null, JSON.stringify(payload, null, 2));
      return;
    }

    await Promise.all(
      payload.map((file) => {
        return new Promise((res) => {
          const info = infos.find((info) => info.id === file.fileId);
          let name = "";
          if (info?.name) {
            name = name + info.name;
          }
          if (info?.fileExtension) {
            name = name + "." + info.fileExtension;
          }
          item.addFileAttachmentAsync(file.url, name, (asyncRes) => {
            if (asyncRes.status === Office.AsyncResultStatus.Failed) {
              useNotificationStore
                .getState()
                .errorNotification("Attach file failed", asyncRes.error, "${file.fileId} ${info?.name}");
              res(null); // we don't reject here just show the usert the error
            } else {
              res(result.result);
            }
          });
        });
      })
    );
  }
};
